import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
    Stack,
    Divider,
    Alert,
    Button,
    IconButton
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

export interface FieldMappingType {
    csvColumn: string;
    mappedField: string;
    customFieldName?: string;
}

export interface FieldMappingProps {
    csvHeaders: string[];
    csvData?: string[][];
    initialMappings?: FieldMappingType[];
    hasUnsubscribeColumn: boolean;
    unsubscribeColumnMapping?: string;
    onMappingsChange: (mappings: FieldMappingType[]) => void;
    onUnsubscribeChange: (hasUnsubscribe: boolean, columnMapping?: string) => void;
}

// Predefined field options in the required order
const PREDEFINED_FIELDS = [
    { value: 'Email Address', label: 'Email Address', required: true },
    { value: 'First Name', label: 'First Name', required: false },
    { value: 'Last Name', label: 'Last Name', required: false },
    { value: 'Company Name', label: 'Company Name', required: false },
    { value: 'Website', label: 'Website', required: false },
    { value: 'Phone Number', label: 'Phone Number', required: false },
    { value: 'LinkedIn Profile', label: 'LinkedIn Profile', required: false },
    { value: 'Location', label: 'Location', required: false }
];

// Auto-matching patterns for CSV headers
const AUTO_MATCH_PATTERNS: Record<string, string[]> = {
    'First Name': ['first name', 'fname', 'f name', 'firstname', 'given name'],
    'Last Name': ['last name', 'lname', 'l name', 'lastname', 'surname', 'family name'],
    'Email Address': ['email', 'email address', 'e-mail', 'mail', 'email_address'],
    'Phone Number': ['phone', 'phone number', 'mobile', 'cell', 'telephone', 'tel'],
    'Company Name': ['company', 'company name', 'organization', 'org', 'business'],
    'Website': ['website', 'web site', 'url', 'site', 'homepage'],
    'LinkedIn Profile': ['linkedin', 'linkedin profile', 'linkedin url', 'li profile'],
    'Location': ['location', 'address', 'city', 'country', 'region', 'place']
};


export default function FieldMapping({
    csvHeaders,
    csvData = [],
    initialMappings = [],
    hasUnsubscribeColumn,
    unsubscribeColumnMapping,
    onMappingsChange,
    onUnsubscribeChange
}: FieldMappingProps) {
    const [mappings, setMappings] = useState<FieldMappingType[]>(initialMappings);
    const [unsubscribeEnabled, setUnsubscribeEnabled] = useState(hasUnsubscribeColumn);
    const [unsubscribeColumn, setUnsubscribeColumn] = useState(unsubscribeColumnMapping || '');
    const [customMappings, setCustomMappings] = useState<FieldMappingType[]>([]);

    // Auto-match function
    const autoMatchField = (header: string): string => {
        const headerLower = header.toLowerCase().trim();

        // Find matching predefined field
        for (const [fieldKey, patterns] of Object.entries(AUTO_MATCH_PATTERNS)) {
            if (patterns.some(pattern => headerLower.includes(pattern))) {
                return fieldKey;
            }
        }

        return '';
    };

    // Initialize mappings for all predefined fields
    useEffect(() => {
        if (initialMappings.length === 0 && csvHeaders.length > 0) {
            const newMappings: FieldMappingType[] = [];
            const usedHeaders = new Set<string>();

            // Create mappings for all predefined fields
            PREDEFINED_FIELDS.forEach(field => {
                // Try to auto-match
                let matchedHeader = '';
                for (const header of csvHeaders) {
                    if (!usedHeaders.has(header) && header !== unsubscribeColumn) {
                        const autoMatched = autoMatchField(header);
                        if (autoMatched === field.value) {
                            matchedHeader = header;
                            usedHeaders.add(header);
                            break;
                        }
                    }
                }

                // Add mapping with matched header or empty string (None)
                newMappings.push({
                    csvColumn: matchedHeader,
                    mappedField: field.value
                });
            });

            setMappings(newMappings);
            onMappingsChange(newMappings);
        }
    }, [csvHeaders, initialMappings, onMappingsChange, unsubscribeColumn]);

    // Get available CSV headers for dropdown (excluding already mapped and unsubscribe column)
    const getAvailableCsvHeaders = (currentCsvColumn?: string) => {
        const allMappings = [...mappings, ...customMappings];
        const mappedColumns = allMappings
            .map(m => m.csvColumn)
            .filter(col => col && col !== currentCsvColumn); // Exclude current column to allow re-selection

        return csvHeaders.filter(header =>
            !mappedColumns.includes(header) &&
            header !== unsubscribeColumn
        );
    };

    // Get preview value from CSV data for a specific column
    const getPreviewValue = (csvColumn: string): string => {
        if (!csvData || csvData.length === 0 || !csvColumn) return '';

        const columnIndex = csvHeaders.indexOf(csvColumn);
        if (columnIndex === -1) return '';

        // Try to get the second row (index 1), fallback to first row (index 0)
        const rowIndex = csvData.length > 1 ? 1 : 0;
        const value = csvData[rowIndex]?.[columnIndex];

        return value || '';
    };

    // Handle mapping change for predefined fields
    const handlePredefinedMappingChange = (mappedField: string, newCsvColumn: string) => {
        const newMappings = mappings.map(m =>
            m.mappedField === mappedField
                ? { ...m, csvColumn: newCsvColumn }
                : m
        );
        setMappings(newMappings);
        onMappingsChange([...newMappings, ...customMappings]);
    };

    // Handle custom mapping change
    const handleCustomMappingChange = (index: number, field: keyof FieldMappingType, value: string) => {
        const newCustomMappings = [...customMappings];
        newCustomMappings[index] = { ...newCustomMappings[index], [field]: value };
        setCustomMappings(newCustomMappings);
        onMappingsChange([...mappings, ...newCustomMappings]);
    };

    // Add a new custom field mapping
    const handleAddCustomMapping = () => {
        const availableHeaders = getAvailableCsvHeaders();

        if (availableHeaders.length > 0) {
            const newMapping: FieldMappingType = {
                csvColumn: availableHeaders[0],
                mappedField: 'Custom Field',
                customFieldName: ''
            };

            const newCustomMappings = [...customMappings, newMapping];
            setCustomMappings(newCustomMappings);
            onMappingsChange([...mappings, ...newCustomMappings]);
        }
    };

    // Remove a custom field mapping
    const handleRemoveCustomMapping = (index: number) => {
        const newCustomMappings = customMappings.filter((_, i) => i !== index);
        setCustomMappings(newCustomMappings);
        onMappingsChange([...mappings, ...newCustomMappings]);
    };

    const handleUnsubscribeToggle = (checked: boolean) => {
        setUnsubscribeEnabled(checked);
        if (!checked) {
            setUnsubscribeColumn('');
            onUnsubscribeChange(false);
        } else {
            onUnsubscribeChange(true, unsubscribeColumn);
        }
    };

    const handleUnsubscribeColumnChange = (column: string) => {
        setUnsubscribeColumn(column);
        onUnsubscribeChange(unsubscribeEnabled, column);
    };

    // Check if email is mapped
    const isEmailMapped = mappings.find(m => m.mappedField === 'Email Address')?.csvColumn;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Field Mapping
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Map your CSV columns to the variables you want to add it on the campaign.
            </Alert>

            {!isEmailMapped && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Email Address is required and must be mapped to a CSV column.
                </Alert>
            )}

            {/* Unsubscribe Column Section */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={unsubscribeEnabled}
                            onChange={(e) => handleUnsubscribeToggle(e.target.checked)}
                        />
                    }
                    label="Does this list have an unsubscribe column?"
                />

                {unsubscribeEnabled && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Unsubscribe Column</InputLabel>
                        <Select
                            value={unsubscribeColumn}
                            label="Unsubscribe Column"
                            onChange={(e) => handleUnsubscribeColumnChange(e.target.value)}
                        >
                            {csvHeaders.map((header) => (
                                <MenuItem key={header} value={header}>
                                    {header}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Predefined Field Mappings */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Predefined Fields
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
                {mappings.map((mapping) => {
                    const field = PREDEFINED_FIELDS.find(f => f.value === mapping.mappedField);
                    const isRequired = field?.required || false;

                    return (
                        <Box key={mapping.mappedField} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Predefined Field
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {mapping.mappedField}
                                        {isRequired && (
                                            <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>

                                <Typography variant="h6" color="text.secondary">
                                    ←
                                </Typography>

                                <Box sx={{ flex: 1 }}>
                                    <FormControl fullWidth required={isRequired} error={isRequired && !mapping.csvColumn}>
                                        <InputLabel shrink={mapping.csvColumn !== undefined}>
                                            CSV Column{isRequired ? ' *' : ''}
                                        </InputLabel>
                                        <Select
                                            value={mapping.csvColumn || ''}
                                            label={`CSV Column${isRequired ? ' *' : ''}`}
                                            onChange={(e) => handlePredefinedMappingChange(mapping.mappedField, e.target.value)}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return <em style={{ color: 'rgba(255, 255, 255, 0.5)' }}>None</em>;
                                                }
                                                return selected;
                                            }}
                                        >
                                            {!isRequired && (
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                            )}
                                            {csvHeaders
                                                .filter(header =>
                                                    header !== unsubscribeColumn &&
                                                    (header === mapping.csvColumn || !mappings.some(m => m.csvColumn === header && m.mappedField !== mapping.mappedField))
                                                )
                                                .map((header) => (
                                                    <MenuItem key={header} value={header}>
                                                        {header}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>

                                    {/* Preview Value */}
                                    {mapping.csvColumn && getPreviewValue(mapping.csvColumn) && (
                                        <Box sx={{
                                            mt: 1,
                                            p: 1,
                                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'rgba(255, 255, 255, 0.12)'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Preview:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.primary' }}>
                                                {getPreviewValue(mapping.csvColumn)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {/* Custom Field Mappings */}
            {customMappings.length > 0 && (
                <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Custom Fields
                    </Typography>

                    <Stack spacing={2} sx={{ mb: 3 }}>
                        {customMappings.map((mapping, index) => (
                            <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Stack direction="column" spacing={2}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ flex: 1 }}>
                                            <TextField
                                                label="Custom Field Name"
                                                value={mapping.customFieldName || ''}
                                                onChange={(e) => handleCustomMappingChange(index, 'customFieldName', e.target.value)}
                                                fullWidth
                                                required
                                            />
                                        </Box>

                                        <Typography variant="h6" color="text.secondary">
                                            ←
                                        </Typography>

                                        <Box sx={{ flex: 1 }}>
                                            <FormControl fullWidth>
                                                <InputLabel shrink={mapping.csvColumn !== undefined}>CSV Column</InputLabel>
                                                <Select
                                                    value={mapping.csvColumn}
                                                    label="CSV Column"
                                                    onChange={(e) => handleCustomMappingChange(index, 'csvColumn', e.target.value)}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <em style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Select a column</em>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    {csvHeaders
                                                        .filter(header =>
                                                            header !== unsubscribeColumn &&
                                                            (header === mapping.csvColumn ||
                                                                (!mappings.some(m => m.csvColumn === header) &&
                                                                    !customMappings.some((m, i) => i !== index && m.csvColumn === header)))
                                                        )
                                                        .map((header) => (
                                                            <MenuItem key={header} value={header}>
                                                                {header}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>

                                            {/* Preview Value */}
                                            {mapping.csvColumn && getPreviewValue(mapping.csvColumn) && (
                                                <Box sx={{
                                                    mt: 1,
                                                    p: 1,
                                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'rgba(255, 255, 255, 0.12)'
                                                }}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Preview:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.primary' }}>
                                                        {getPreviewValue(mapping.csvColumn)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        <IconButton
                                            onClick={() => handleRemoveCustomMapping(index)}
                                            color="error"
                                            size="small"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </>
            )}

            {/* Add Custom Field Button */}
            {getAvailableCsvHeaders().length > 0 && (
                <Button
                    startIcon={<Add />}
                    onClick={handleAddCustomMapping}
                    variant="outlined"
                    sx={{ mt: 2 }}
                >
                    Add Custom Field
                </Button>
            )}
        </Box>
    );
}
